import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom, timeout } from 'rxjs';
import { appRoutes } from './app.routes';
import { AuthService, UsersMeService, httpErrorInterceptor } from '@zag/auth/ui';
import { LanguageService, ThemeService, languageHeaderInterceptor } from '@zag/ui';
import { provideBreadcrumbConfig } from './breadcrumb.providers';
import { TenantStatusService } from './tenant-status.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideBreadcrumbConfig(),
    // Provide HttpClient at the top level so the interceptors are actually
    // applied. The Accept-Language interceptor stamps every request with the
    // user's currently selected UI language so the backend can resolve
    // per-entity translations correctly.
    provideHttpClient(withInterceptors([languageHeaderInterceptor, httpErrorInterceptor])),
    // NOTE: Do NOT pass `fallbackLang` here. Doing so makes TranslateService's
    // constructor call setFallbackLang() which fires an HTTP request synchronously
    // during DI resolution. That request races with subscriber wiring and the
    // resulting translations never make it into the store (they're cached in the
    // shareReplay'd Observable but never reach `store.setTranslations`). Loading
    // the fallback + active language explicitly from the app initializer below
    // avoids this entire problem because the service is fully constructed by then.
    provideTranslateService({}),
    provideTranslateHttpLoader({
      prefix: '/api/v1/i18n/',
      suffix: '',
    }),
    // Load translations with browser language detection
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      const languageService = inject(LanguageService);

      try {
        // Initialize language (detects browser language or uses stored preference)
        const language = languageService.initializeLanguage();

        // Set fallback language and load active-language translations after the
        // service is fully constructed (avoids constructor-time race condition).
        translate.setFallbackLang('en');
        await firstValueFrom(translate.use(language).pipe(timeout(5000)));
      } catch (error) {
        console.warn('Failed to load translations, using keys as fallback:', error);
      }
    }),
    // Initialize theme (detect system preference or use stored preference)
    provideAppInitializer(() => {
      const themeService = inject(ThemeService);

      // Initialize theme from localStorage or system preference
      const theme = themeService.initializeTheme();

      // Apply theme to document
      themeService.applyThemeToDocument(theme);

      // Add theme transition class to root for smooth transitions
      if (typeof document !== 'undefined') {
        document.documentElement.classList.add('theme-transition');
      }
    }),
    // Check for existing session on app startup.
    // Skip the session check when the browser is on a tenant subdomain that
    // is not yet provisioned — the main API's auth endpoint will return 404
    // or 500 for unprovisioned tenants, which delays Angular bootstrap
    // unnecessarily. The provisioning guard will redirect to /provisioning
    // and the session will be loaded after the tenant is ready.
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const usersMeService = inject(UsersMeService);
      const tenantStatusService = inject(TenantStatusService);

      // When on a tenant domain, check tenant status first to avoid
      // hitting the main API when the tenant is not yet provisioned.
      if (tenantStatusService.host) {
        const resource = tenantStatusService.statusResource;

        // Wait for the status resource to settle (max 3 seconds).
        const UNSETTLED = new Set(['idle', 'loading', 'reloading']);
        const deadline = Date.now() + 3000;
        while (UNSETTLED.has(resource.status()) && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (tenantStatusService.status() !== 'ready') {
          console.debug('Tenant not ready, skipping session check');
          return;
        }
      }

      try {
        await authService.getSession();
        // Sync server-side language/theme preferences (best-effort, non-blocking)
        void usersMeService.syncServerPreferences();
      } catch {
        // Session check failed - user not logged in, this is fine
        console.debug('No active session found');
      }
    }),
  ],
};
